import React, { useEffect, useMemo, useState } from 'react';
import { Loader2 } from 'lucide-react';
import { salesAPI, SalesReport, SalesReportRow } from '../../services/salesApi';
import { Column, DataTable, FilterSelect, ListToolbar, Notice, Stat, Stats } from '../shared/listPage';
import {
  ACTIVE_STATUSES, LOST_REASON_OPTIONS, STATUS_COLORS, STATUS_OPTIONS, LeadStatus, formatMonthly, kindLabel, sourceLabel,
  statusLabel,
} from './salesModel';

/**
 * Πωλήσεις → «Αναφορές» (απόφαση χρήστη 3/11/2026): πώς πάνε οι πωλήσεις — τι κερδίσαμε/χάσαμε στην περίοδο, πόσα
 * είναι σε εξέλιξη (και πόσο αξίζουν), από ποια πηγή και ποιο είδος κλείνουμε, ΓΙΑΤΙ χάνουμε (με τις σημειώσεις
 * «τι έλειπε» — λίστα για το προϊόν) και πόσες επαφές γίνονται την εβδομάδα. Όλα τα νούμερα από τον server
 * (GET /api/sales/reports)· εδώ μόνο παρουσίαση, με μπάρες CSS όπως οι υπόλοιπες αναλύσεις της εφαρμογής.
 */

const PERIODS = [
  { value: '30', label: 'Τελευταίες 30 ημέρες' },
  { value: '90', label: 'Τελευταίες 90 ημέρες' },
  { value: '365', label: 'Τελευταίο έτος' },
  { value: 'all', label: 'Από την αρχή' },
];
const pct = (a: number, b: number) => (b > 0 ? `${Math.round((a / b) * 100)}%` : '—');
const lostLabel = (r: string | null) => LOST_REASON_OPTIONS.find(o => o.value === r)?.label || r || '—';
const weekLabel = (ymd: string) => new Date(`${ymd}T12:00:00Z`).toLocaleDateString('el-GR', { day: 'numeric', month: 'short' });

const Bar: React.FC<{ value: number; max: number; color?: string; className?: string }> = ({ value, max, color, className = '' }) => (
  <div className="h-1.5 bg-surface-2 rounded-full overflow-hidden">
    <div className={`h-full rounded-full ${className}`} style={{ width: `${max > 0 ? (value / max) * 100 : 0}%`, background: color }} />
  </div>
);

const Card: React.FC<{ title: string; hint?: string; children: React.ReactNode; testId?: string }> = ({ title, hint, children, testId }) => (
  <section className="rounded-xl border border-line bg-surface-1 p-4 space-y-3" data-testid={testId}>
    <div>
      <h2 className="text-sm font-semibold text-t1">{title}</h2>
      {hint && <p className="text-xs text-t3 mt-0.5">{hint}</p>}
    </div>
    {children}
  </section>
);

interface Props {
  onOpen: (id: number) => void;
}

const SalesReports: React.FC<Props> = ({ onOpen }) => {
  const [period, setPeriod] = useState('90');
  const [data, setData] = useState<SalesReport | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    salesAPI.getReports(period === 'all' ? null : Number(period))
      .then(d => { if (!cancelled) { setData(d); setError(null); } })
      .catch(() => { if (!cancelled) setError('Οι αναφορές δεν φόρτωσαν'); })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [period]);

  const stages = useMemo(() => {
    const by = new Map((data?.stages || []).map(s => [s.status, s]));
    return STATUS_OPTIONS.map(o => ({ status: o.value as LeadStatus, n: by.get(o.value)?.n || 0, value: by.get(o.value)?.value || 0,
      valued: by.get(o.value)?.valued || 0 }));
  }, [data]);
  const active = stages.filter(s => ACTIVE_STATUSES.includes(s.status));
  const activeValue = active.reduce((t, s) => t + s.value, 0);
  const activeCount = active.reduce((t, s) => t + s.n, 0);
  const activeValued = active.reduce((t, s) => t + s.valued, 0);
  const wonNow = stages.find(s => s.status === 'won');
  const maxStage = Math.max(1, ...stages.map(s => s.n));
  const maxReason = Math.max(1, ...(data?.lost_reasons || []).map(r => r.n));
  const maxWeek = Math.max(1, ...(data?.weekly || []).map(w => w.calls + w.meetings + w.other));

  const breakdownColumns = (label: string, render: (k: string) => string): Column<SalesReportRow>[] => [
    { key: 'key', header: label, width: 'minmax(160px, 2fr)', stack: 'title', render: r => <span className="font-medium text-t1">{render(r.key)}</span>, stackRender: r => render(r.key) },
    { key: 'total', header: 'Υποψήφιοι', width: '110px', align: 'right', render: r => <span className="tabular-nums">{r.total}</span> },
    { key: 'active', header: 'Σε εξέλιξη', width: '110px', align: 'right', hideBelow: 'medium', render: r => <span className="tabular-nums text-t2">{r.active}</span> },
    { key: 'won', header: 'Πελάτες', width: '100px', align: 'right', render: r => <span className="tabular-nums text-t1">{r.won}</span> },
    { key: 'lost', header: 'Χάθηκαν', width: '100px', align: 'right', hideBelow: 'narrow', render: r => <span className="tabular-nums text-t2">{r.lost}</span> },
    {
      key: 'rate', header: 'Επιτυχία', width: '100px', align: 'right',
      render: r => <span className="tabular-nums text-t1">{pct(r.won, r.won + r.lost)}</span>,
      stackRender: r => `επιτυχία ${pct(r.won, r.won + r.lost)}`,
    },
  ];

  const s = data?.summary;
  return (
    <>
      <ListToolbar
        filters={<FilterSelect label="Περίοδος" value={period} onChange={setPeriod} options={PERIODS} />}
        summary={loading ? (
          <span className="inline-flex items-center gap-1.5 text-t3"><Loader2 className="w-3.5 h-3.5 animate-spin" aria-hidden="true" /> Υπολογισμός…</span>
        ) : undefined}
      />
      {error && <Notice tone="danger" testId="sales-reports-error">{error}</Notice>}
      {data && s && (
        <div className="space-y-4" data-testid="sales-reports">
          <Card title="Η περίοδος" testId="sales-rep-summary"
            hint="Πελάτες/χαμένοι: όσοι πέρασαν στο στάδιο μέσα στην περίοδο και είναι ακόμη εκεί.">
            <Stats>
              <Stat label="Νέοι υποψήφιοι" value={s.new_leads} />
              <Stat label="Έγιναν πελάτες" value={s.won} />
              <Stat label="Χάθηκαν" value={s.lost} />
              <Stat label="Επιτυχία" value={pct(s.won, s.won + s.lost)} />
              {s.won_value > 0 && <Stat label="Νέα αξία" value={formatMonthly(s.won_value)} />}
              {s.median_days_to_win !== null && <Stat label="Μέρες ως πελάτης (διάμεσος)" value={Math.round(s.median_days_to_win)} />}
              <Stat label="Επαφές" value={s.contacts} />
            </Stats>
          </Card>

          <div className="grid gap-4 lg:grid-cols-2">
            <Card title="Ροή τώρα" testId="sales-rep-stages"
              hint={activeValue > 0
                ? `Σε εξέλιξη: ${formatMonthly(activeValue)} (εκτίμηση σε ${activeValued} από ${activeCount})`
                : 'Βάλε «Αξία (€/μήνα)» στους υποψηφίους για να βγαίνει η αξία σε εξέλιξη.'}>
              <div className="space-y-2">
                {stages.map(st => (
                  <div key={st.status}>
                    <div className="flex items-center justify-between text-xs mb-1">
                      <span className="text-t2">{statusLabel(st.status)}</span>
                      <span className="text-t3 tabular-nums">{st.n}{st.value > 0 ? ` · ${formatMonthly(st.value)}` : ''}</span>
                    </div>
                    <Bar value={st.n} max={maxStage} color={STATUS_COLORS[st.status]} />
                  </div>
                ))}
              </div>
              {wonNow && wonNow.value > 0 && (
                <p className="text-xs text-t3">Πελάτες από τις πωλήσεις: {wonNow.n} · {formatMonthly(wonNow.value)}</p>
              )}
            </Card>

            <Card title="Γιατί χάνουμε" testId="sales-rep-lost">
              {(data.lost_reasons || []).length === 0 ? <p className="text-sm text-t3">Κανένας χαμένος στην περίοδο.</p> : (
                <div className="space-y-2">
                  {data.lost_reasons.map(r => (
                    <div key={r.reason || 'none'}>
                      <div className="flex items-center justify-between text-xs mb-1">
                        <span className="text-t2">{lostLabel(r.reason)}</span>
                        <span className="text-t3 tabular-nums">{r.n}</span>
                      </div>
                      <Bar value={r.n} max={maxReason} color={STATUS_COLORS.lost} />
                    </div>
                  ))}
                </div>
              )}
              {(data.lost_notes || []).length > 0 && (
                <div className="pt-2 border-t border-line">
                  <p className="xs-label mb-1">Τι έλειπε / ποιος ανταγωνιστής / τιμή</p>
                  <ul className="space-y-1.5 text-sm" data-testid="sales-rep-lost-notes">
                    {data.lost_notes.map(n => (
                      <li key={n.id}>
                        <button type="button" className="font-medium text-t1 hover:underline" onClick={() => onOpen(n.id)}>{n.name}</button>
                        <span className="text-t3"> · {lostLabel(n.reason)}: </span>
                        <span className="text-t2">{n.note}</span>
                      </li>
                    ))}
                  </ul>
                </div>
              )}
            </Card>
          </div>

          {/* Ολόκληρο πλάτος: σε μισό, ο πίνακας περνά στη στοίβα και χάνονται οι τίτλοι των στηλών. */}
          <div className="space-y-4">
            <Card title="Ανά πηγή" hint="Όσοι μπήκαν στην περίοδο και πού βρίσκονται τώρα." testId="sales-rep-source">
              <DataTable columns={breakdownColumns('Πηγή', sourceLabel)} rows={data.by_source} rowKey={r => r.key} dense
                empty={<p className="px-4 py-4 text-sm text-t3">Κανένας υποψήφιος στην περίοδο.</p>} />
            </Card>
            <Card title="Ανά είδος" hint="Όσοι μπήκαν στην περίοδο και πού βρίσκονται τώρα." testId="sales-rep-kind">
              <DataTable columns={breakdownColumns('Είδος', kindLabel)} rows={data.by_kind} rowKey={r => r.key} dense
                empty={<p className="px-4 py-4 text-sm text-t3">Κανένας υποψήφιος στην περίοδο.</p>} />
            </Card>
          </div>

          <Card title="Επαφές ανά εβδομάδα" hint="Οι 12 τελευταίες εβδομάδες (κλήσεις, συναντήσεις/demo, σημειώσεις/email)." testId="sales-rep-weekly">
            {(data.weekly || []).length === 0 ? <p className="text-sm text-t3">Καμία επαφή ακόμη.</p> : (
              <div className="space-y-2">
                {data.weekly.map(w => {
                  const total = w.calls + w.meetings + w.other;
                  return (
                    <div key={w.week}>
                      <div className="flex items-center justify-between text-xs mb-1">
                        <span className="text-t2">Εβδομάδα {weekLabel(w.week)}</span>
                        <span className="text-t3 tabular-nums">{w.calls} κλήσεις · {w.meetings} συναντήσεις · {w.other} άλλες</span>
                      </div>
                      <Bar value={total} max={maxWeek} className="bg-brand" />
                    </div>
                  );
                })}
              </div>
            )}
          </Card>
        </div>
      )}
    </>
  );
};

export default SalesReports;
